import dJSON from 'dirty-json';

import Splide from '@splidejs/splide';

const moduleSelector = '.how-it-works';
const AUTOPLAY_INTERVAL = 5000;

const timelineSvg = /* html */ `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 508 250" fill="none" preserveAspectRatio="xMaxYMid meet" aria-hidden="true">
    <path d="M453.215 1H-1427V249H453.215L506 125L453.215 1Z" fill="url(#how-it-works-timeline-fill)" fill-opacity="0.15" stroke-width="2" stroke="url(#how-it-works-timeline-stroke)"/>
    <defs>
      <linearGradient id="how-it-works-timeline-fill" x1="506" y1="125" x2="-16" y2="125" gradientUnits="userSpaceOnUse">
        <stop offset="0.456731" stop-color="#E81B2A"/>
        <stop offset="1" stop-color="#E81B2A" stop-opacity="0"/>
      </linearGradient>
      <linearGradient id="how-it-works-timeline-stroke" x1="-15.9999" y1="125" x2="506" y2="125" gradientUnits="userSpaceOnUse">
        <stop stop-color="#E81B2A" stop-opacity="0"/>
        <stop offset="1" stop-color="#E81B2A"/>
      </linearGradient>
    </defs>
  </svg>
`;

/**
 * Module Init
 */
(() => {
  const modules = document.querySelectorAll(moduleSelector);
  if (!modules.length) return;

  modules.forEach((query) => {
    try {
      query.querySelector('script')?.remove(); // cleanup

      const { data } = dJSON.parse(query.data);
      const list = query.querySelector('.splide__list');

      data.forEach((item) => {
        list.insertAdjacentHTML('beforeend', /* html */ `
          <div class="splide__slide">
            <div class="how-it-works-item">
              ${item.title ? `<h3 class="title">${item.title}</h3>` : ''}
              ${item.details ? `<p class="details">${item.details}</p>` : ''}
              ${item.button_text ? `<a href="${item.button_link}" class="link">${item.button_text}</a>` : ''}
            </div>
          </div>
        `);
      });

      const splideEl = query.querySelector('.splide');
      if (!splideEl || !list.children.length) return;

      ensureTimeline(splideEl);

      const slider = new Splide(splideEl, {
        type: 'slide',
        perPage: 4,
        perMove: 1,
        gap: 50,
        pagination: false,
        arrows: false,
        height: 250,
        // Native Splide autoplay only starts when slides.length > perPage.
        // This module is a 4-up step row, so we drive autoplay ourselves
        // using the same 0–1 progress Integrity uses from autoplay:playing.
        autoplay: false,
        interval: AUTOPLAY_INTERVAL,
        rewind: true,
        speed: 650,
        // Breakpoints: put the active step at the left of the track.
        // trimSpace false so the last step can sit there too.
        focus: 0,
        trimSpace: false,
        breakpoints: {
          1200: {
            perPage: 2,
            gap: 32,
          },
          991: {
            perPage: 1,
            gap: 24,
          },
        },
      });

      slider.on('mounted', () => {
        bindTimeline(slider);
      });

      slider.mount();
    } catch (error) {
      console.error(`Error: ${moduleSelector}`, error);
    }
  });

  setTimeout(() => {
    window.dispatchEvent(new Event('resize'));
  }, 500);
})();

function ensureTimeline(splideEl) {
  let timeline = splideEl.querySelector('.how-it-works__timeline');
  if (!timeline) {
    splideEl.insertAdjacentHTML('beforeend', /* html */ `
      <div class="how-it-works__timeline">
        <div class="how-it-works__timeline-bar"></div>
      </div>
    `);
    timeline = splideEl.querySelector('.how-it-works__timeline');
  }

  const bar = timeline.querySelector('.how-it-works__timeline-bar');
  if (bar) {
    bar.innerHTML = timelineSvg;
  }
}

function getRealSlides(splide) {
  return splide.Components.Slides.get()
    .filter((slide) => !slide.isClone)
    .map((slide) => slide.slide);
}

function allStepsVisible(splide) {
  return splide.length <= (splide.options.perPage || 1);
}

/**
 * Step highlight is independent of Splide's index.
 * Desktop 4-up stays pinned. At breakpoints, shift one slide at a time
 * so the active step is always the leftmost card.
 */
function syncPage(splide, stepIndex) {
  if (allStepsVisible(splide)) {
    splide.Components.Move.translate(0);
    return;
  }

  if (splide.index !== stepIndex) {
    splide.go(stepIndex);
  }
}

function setActiveStep(splide, stepIndex) {
  getRealSlides(splide).forEach((slide, index) => {
    slide.classList.toggle('is-active', index === stepIndex);
  });
}

function slideRight(slide) {
  return slide.offsetLeft + slide.offsetWidth;
}

function isSingleSlide(splide) {
  return (splide.options.perPage || 1) <= 1;
}

/** Right edge on 2+/4-up; center of the card on 1-up (991). */
function slideAnchor(splide, slide) {
  if (isSingleSlide(splide)) {
    return slide.offsetLeft + slide.offsetWidth / 2;
  }

  return slideRight(slide);
}

function getListContentWidth(splide) {
  const slides = getRealSlides(splide);
  if (!slides.length) return 0;
  return slideRight(slides[slides.length - 1]);
}

/**
 * Keep the chevron in list space so paging does not snap the bar
 * backwards in the viewport.
 */
function syncTimelineToList(splide, bar) {
  const timeline = bar.parentElement;
  const list = splide.Components.Elements.list;
  if (!timeline || !list) return;

  const position = splide.Components.Move.getPosition();
  const splideRect = splide.root.getBoundingClientRect();
  const listRect = list.getBoundingClientRect();
  const listLeft = listRect.left - splideRect.left - position;

  timeline.style.left = `${listLeft}px`;
  timeline.style.right = 'auto';
  timeline.style.width = `${getListContentWidth(splide)}px`;
  timeline.style.transform = `translate(${position}px, -50%)`;
}

/**
 * Integrity hero-slider: autoplay:playing(rate) grows a beam from the current
 * nav button toward the next. Here the slides themselves are those anchors.
 */
function bindTimeline(splide) {
  const bar = splide.root.querySelector('.how-it-works__timeline-bar');
  if (!bar) return;

  const slides = getRealSlides(splide);
  let stepIndex = 0;

  const update = (rate = 0) => {
    setActiveStep(splide, stepIndex);
    if (allStepsVisible(splide)) {
      splide.Components.Move.translate(0);
    }
    syncTimelineToList(splide, bar);
    updateTimelineBar(splide, bar, rate, stepIndex);
  };

  const goToStep = (index) => {
    const last = slides.length - 1;
    stepIndex = index < 0 ? last : index > last ? 0 : index;
    setActiveStep(splide, stepIndex);
    syncPage(splide, stepIndex);
    autoplay.restartStep();
    update(0);
  };

  const autoplay = startStepAutoplay(
    splide,
    bar,
    () => stepIndex,
    update,
    () => goToStep(stepIndex + 1),
  );

  slides.forEach((slide, index) => {
    slide.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;
      if (stepIndex === index) {
        autoplay.restartStep();
        update(0);
        return;
      }
      goToStep(index);
    });
  });

  splide.on('move moved', () => {
    setActiveStep(splide, stepIndex);
    syncTimelineToList(splide, bar);
  });

  splide.on('resized', () => {
    syncPage(splide, stepIndex);
    update(0);
  });

  syncPage(splide, stepIndex);
  update(0);
}

function getTimelineAnchors(splide, bar, stepIndex) {
  const slides = getRealSlides(splide);
  const timeline = bar.parentElement;
  const current = slides[stepIndex];

  if (!timeline || !current) return null;

  const start = slideAnchor(splide, current);
  const end = stepIndex >= slides.length - 1
    ? (isSingleSlide(splide) ? start : getListContentWidth(splide))
    : slideAnchor(splide, slides[stepIndex + 1]);

  return { start, end };
}

/** Match the first step’s px/s so the final (longer) segment does not crawl. */
function getTimelinePixelsPerMs(splide, bar) {
  const slides = getRealSlides(splide);
  const interval = splide.options.interval || AUTOPLAY_INTERVAL;

  if (slides.length < 2) {
    return 1 / interval;
  }

  const stepPx = Math.max(
    slideAnchor(splide, slides[1]) - slideAnchor(splide, slides[0]),
    1,
  );
  return stepPx / interval;
}

function getSegmentDuration(splide, bar, stepIndex) {
  const interval = splide.options.interval || AUTOPLAY_INTERVAL;
  const anchors = getTimelineAnchors(splide, bar, stepIndex);

  if (!anchors) return interval;

  const distance = Math.max(anchors.end - anchors.start, 0);
  if (distance <= 0) return interval;

  return distance / getTimelinePixelsPerMs(splide, bar);
}

function startStepAutoplay(splide, bar, getStepIndex, onRate, onAdvance) {
  const root = splide.root;
  let startedAt = performance.now();
  let segmentDuration = getSegmentDuration(splide, bar, getStepIndex());
  let paused = false;
  let pausedElapsed = 0;
  let waitingForAdvance = false;
  let rafId = 0;

  const pause = () => {
    if (paused) return;
    pausedElapsed = performance.now() - startedAt;
    paused = true;
  };

  const resume = () => {
    if (!paused) return;
    startedAt = performance.now() - pausedElapsed;
    paused = false;
  };

  const restartStep = () => {
    waitingForAdvance = false;
    pausedElapsed = 0;
    startedAt = performance.now();
    segmentDuration = getSegmentDuration(splide, bar, getStepIndex());
  };

  root.addEventListener('mouseenter', pause);
  root.addEventListener('mouseleave', resume);
  root.addEventListener('focusin', pause);
  root.addEventListener('focusout', resume);

  splide.on('resized', () => {
    segmentDuration = getSegmentDuration(splide, bar, getStepIndex());
  });

  splide.on('destroy', () => {
    cancelAnimationFrame(rafId);
  });

  const tick = (now) => {
    rafId = requestAnimationFrame(tick);
    if (paused || waitingForAdvance) return;

    const rate = Math.min((now - startedAt) / segmentDuration, 1);
    onRate(rate);

    if (rate >= 1) {
      waitingForAdvance = true;
      onRate(1);
      onAdvance();
    }
  };

  rafId = requestAnimationFrame(tick);

  return { restartStep };
}

function updateTimelineBar(splide, bar, progress, stepIndex) {
  const anchors = getTimelineAnchors(splide, bar, stepIndex);

  if (!anchors) {
    bar.style.width = '0px';
    return;
  }

  const { start, end } = anchors;
  const additionalWidth = (end - start) * progress;
  bar.style.width = `${Math.max(0, start + additionalWidth)}px`;
}
